import React, { useEffect, useState } from 'react';
import { adminApi, productApi } from '../../api';
import { Product } from '../../types';
import Loading from '../../components/common/Loading';
import { toImageUrl } from '../../utils/image';

interface AdminCategory {
  id: number;
  code: string;
  parentId: number | null;
  displayOrder: number;
  active: boolean;
  imageUrl: string | null;
  translations: { locale: string; name: string }[];
  children?: AdminCategory[];
  expanded?: boolean;
  products?: Product[];
  productsLoaded?: boolean;
}

const getName = (cat: AdminCategory) =>
  cat.translations.find(t => t.locale === 'EN')?.name ||
  cat.translations.find(t => t.locale === 'KO')?.name ||
  cat.code;

const buildTree = (flat: AdminCategory[]): AdminCategory[] => {
  const map = new Map<number, AdminCategory>();
  flat.forEach(c => map.set(c.id, { ...c, children: [] }));
  const roots: AdminCategory[] = [];
  map.forEach(c => {
    if (c.parentId && map.has(c.parentId)) {
      map.get(c.parentId)!.children!.push(c);
    } else {
      roots.push(c);
    }
  });
  return roots;
};

const emptyForm = { code: '', nameEn: '', parentId: '' };

const AdminCategories: React.FC = () => {
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [allFlat, setAllFlat] = useState<AdminCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminCategory | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await adminApi.getAdminCategories();
    const flat = res.data.data as unknown as AdminCategory[];
    setAllFlat(flat);
    setCategories(buildTree(flat));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = (parentId = '') => {
    setEditTarget(null);
    setForm({ ...emptyForm, parentId });
    setShowForm(true);
  };

  const openEdit = (cat: AdminCategory) => {
    setEditTarget(cat);
    setForm({
      code: cat.code,
      nameEn: cat.translations.find(t => t.locale === 'EN')?.name
        || cat.translations.find(t => t.locale === 'KO')?.name || '',
      parentId: cat.parentId ? String(cat.parentId) : '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const translations = [{ locale: 'EN', name: form.nameEn }];
    const payload = {
      code: form.code,
      parentId: form.parentId ? Number(form.parentId) : undefined,
      translations,
    } as any;
    try {
      if (editTarget) {
        await adminApi.updateCategory(editTarget.id, payload);
      } else {
        await adminApi.createCategory(payload);
      }
      setShowForm(false);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await adminApi.deleteCategory(id);
    load();
  };

  const toggleProducts = (cat: AdminCategory) => {
    const update = (list: AdminCategory[]): AdminCategory[] =>
      list.map(c => {
        if (c.id === cat.id) {
          if (!c.productsLoaded) {
            productApi.getList({ categoryId: c.id, size: 100 }).then(res => {
              setCategories(prev => markLoaded(prev, cat.id, res.data.data.content));
            });
            return { ...c, expanded: true };
          }
          return { ...c, expanded: !c.expanded };
        }
        return { ...c, children: c.children ? update(c.children) : [] };
      });
    setCategories(prev => update(prev));
  };

  const markLoaded = (list: AdminCategory[], id: number, products: Product[]): AdminCategory[] =>
    list.map(c => {
      if (c.id === id) return { ...c, products, productsLoaded: true };
      return { ...c, children: c.children ? markLoaded(c.children, id, products) : [] };
    });

  const renderCategory = (cat: AdminCategory, depth = 0): React.ReactNode => (
    <div key={cat.id}>
      <div style={{ ...styles.row, paddingLeft: 20 + depth * 24 }}>
        <div style={styles.rowLeft}>
          {depth > 0 && <span style={styles.indent}>└</span>}
          <span style={styles.catName}>{getName(cat)}</span>
          <span style={styles.catCode}>{cat.code}</span>
          {!cat.active && <span style={styles.inactiveBadge}>비활성</span>}
          {cat.children?.length ? (
            <span style={styles.childCount}>하위 {cat.children.length}개</span>
          ) : null}
        </div>
        <div style={styles.rowRight}>
          <button style={styles.expandBtn} onClick={() => toggleProducts(cat)}>
            {cat.expanded ? '▲ 접기' : '▼ 상품'}
          </button>
          <button style={styles.editBtn} onClick={() => openEdit(cat)}>수정</button>
          <button style={styles.addSubBtn} onClick={() => openCreate(String(cat.id))}>+ 하위</button>
          <button style={styles.delBtn} onClick={() => handleDelete(cat.id)}>삭제</button>
        </div>
      </div>

      {cat.expanded && (
        <div style={{ ...styles.productPanel, marginLeft: 20 + depth * 24 }}>
          {!cat.productsLoaded ? (
            <p style={styles.loadingText}>불러오는 중...</p>
          ) : cat.products?.length === 0 ? (
            <p style={styles.emptyText}>소속 상품 없음</p>
          ) : (
            <table style={styles.productTable}>
              <thead>
                <tr>{['ID', '썸네일', '상품명', '브랜드', '가격', '재고'].map(h => (
                  <th key={h} style={styles.pth}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {cat.products?.map(p => (
                  <tr key={p.id} style={styles.ptr}>
                    <td style={styles.ptd}>{p.id}</td>
                    <td style={styles.ptd}><img src={toImageUrl(p.thumbnailUrl)} alt="" style={styles.pthumb} /></td>
                    <td style={styles.ptd}>{p.name}</td>
                    <td style={styles.ptd}>{p.brandName || '-'}</td>
                    <td style={styles.ptd}>
                      {(p.discountPercent ?? 0) > 0
                        ? <span style={{ color: '#b39ddb', fontWeight: 700 }}>${p.price.toFixed(2)}</span>
                        : `$${p.originalPrice.toFixed(2)}`}
                    </td>
                    <td style={styles.ptd}>{p.baseStock ?? '-'}</td>
                    <td style={styles.ptd}>-</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {cat.children?.map(child => renderCategory(child, depth + 1))}
    </div>
  );

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>카테고리 관리</h2>
        <button style={styles.addBtn} onClick={() => openCreate()}>+ 카테고리 추가</button>
      </div>

      {loading ? <Loading /> : (
        <div style={styles.treeWrap}>
          {categories.length === 0 ? (
            <div style={styles.empty}>
              <p>등록된 카테고리가 없습니다.</p>
              <button style={styles.addBtn} onClick={() => openCreate()}>첫 카테고리 만들기</button>
            </div>
          ) : categories.map(cat => renderCategory(cat))}
        </div>
      )}

      {showForm && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>
              {editTarget ? '카테고리 수정' : form.parentId ? '하위 카테고리 추가' : '카테고리 추가'}
            </h3>
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>코드 (영문, 고유값) *</label>
                <input style={styles.input} required value={form.code}
                  onChange={e => setForm({ ...form, code: e.target.value })}
                  placeholder="예: skincare" />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>카테고리명 (English) *</label>
                <input style={styles.input} required value={form.nameEn}
                  onChange={e => setForm({ ...form, nameEn: e.target.value })}
                  placeholder="e.g. Skincare" />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>상위 카테고리</label>
                <select style={styles.input} value={form.parentId}
                  onChange={e => setForm({ ...form, parentId: e.target.value })}>
                  <option value="">없음 (최상위)</option>
                  {allFlat.filter(c => c.id !== editTarget?.id).map(c => (
                    <option key={c.id} value={c.id}>
                      {c.parentId ? `  └ ${getName(c)}` : getName(c)}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.modalBtns}>
                <button type="button" style={styles.cancelBtn} onClick={() => setShowForm(false)}>취소</button>
                <button type="submit" style={styles.saveBtn} disabled={saving}>
                  {saving ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700 },
  addBtn: { padding: '10px 20px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  treeWrap: { background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', overflow: 'hidden' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f9f9f9' },
  rowLeft: { display: 'flex', alignItems: 'center', gap: 10, flex: 1 },
  indent: { color: '#ccc', fontSize: 14 },
  catName: { fontSize: 14, fontWeight: 600, color: '#333' },
  catCode: { fontSize: 11, color: '#aaa', background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 },
  inactiveBadge: { fontSize: 11, color: '#fff', background: '#ccc', padding: '2px 6px', borderRadius: 4 },
  childCount: { fontSize: 11, color: '#b39ddb', background: '#f3ecfb', padding: '2px 8px', borderRadius: 10 },
  rowRight: { display: 'flex', gap: 6, alignItems: 'center' },
  expandBtn: { padding: '4px 10px', background: '#f0f4ff', color: '#5c6bc0', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  editBtn: { padding: '4px 10px', background: '#ece3f7', color: '#b39ddb', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  addSubBtn: { padding: '4px 10px', background: '#e8f5e9', color: '#4caf50', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  delBtn: { padding: '4px 10px', background: '#fff0f0', color: '#e53935', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  productPanel: { background: '#f9f9fc', borderLeft: '3px solid #b39ddb', margin: '0 20px 12px', borderRadius: '0 8px 8px 0', padding: '12px 16px' },
  loadingText: { fontSize: 13, color: '#aaa' },
  emptyText: { fontSize: 13, color: '#bbb', padding: '8px 0' },
  productTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  pth: { padding: '8px 10px', textAlign: 'left', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' },
  ptr: { borderBottom: '1px solid #f5f5f5' },
  ptd: { padding: '8px 10px', color: '#444' },
  pthumb: { width: 36, height: 36, objectFit: 'cover', borderRadius: 4 },
  empty: { padding: '60px 20px', textAlign: 'center', color: '#bbb' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: 18, fontWeight: 700, marginBottom: 24 },
  form: { display: 'flex', flexDirection: 'column', gap: 18 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 500, color: '#555' },
  input: { padding: '11px 14px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, outline: 'none' },
  modalBtns: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 },
  cancelBtn: { padding: '10px 20px', border: '1px solid #e8e8e8', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 },
  saveBtn: { padding: '10px 24px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
};

export default AdminCategories;
